import { useMutation } from '@apollo/client';
import {
  Button,
  HStack,
  ModalFooter,
  SimpleGrid,
  useToast,
  FormControl,
  FormLabel,
  Switch,
  Checkbox,
  Flex,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  CREATE_PAGE_GROUP,
  REMOVE_GROUP_ELEMENT,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import sanitizeString from '../../../utils/sanitizeString';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import Select from '../../Form/Select';
import context from '../context';

export const redirectTitleTypes = [
  {
    value: 'default',
    label: 'Normal',
  },
  {
    value: 'with-image',
    label: 'Com imagem',
  },
  {
    value: 'with-search',
    label: 'Com caixa de pesquisa',
  },
];

interface Props {
  onCreated?: (data: any) => void;
}
interface FormData {
  name: string;
  countdown: boolean;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
  padding_top?: number;
  padding_bottom?: number;
  padding_right?: number;
  padding_left?: number;
  border_top?: string;
  border_bottom?: string;
  border_left?: string;
  border_right?: string;
  height?: string;
  type: {
    label: string | string;
    value: string | string;
  };
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  schedule: yup.boolean(),
  countdown: yup.boolean(),
  type: yup
    .object()
    .shape({
      value: yup.string().required('Tipo é obrigatório'),
      label: yup.string().required('Tipo é obrigatório'),
    })
    .required('Tipo é obrigatório'),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('Data de início é obrigatória'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(schedule_start, 'Data final deve ser superior a data inicial')
          .required('Data Final é obrigatória');
      }
      return yup.date().required('Data Final é obrigatória');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();

  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);
  const { formState, handleSubmit, register, control, setValue } = useForm({
    resolver: yupResolver(formData),
  });

  const [createPageGroup, { loading: createLoading }] = useMutation(
    CREATE_PAGE_GROUP,
    {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    },
  );

  const [updatePageGroup, { loading: updateLoading }] = useMutation(
    UPDATE_PAGE_GROUP,
    {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    },
  );

  const [removeGroupElement, { loading: removeLoading }] = useMutation(
    REMOVE_GROUP_ELEMENT,
    {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    },
  );

  const [removePageGroup, { loading: deleteLoading }] = useMutation(
    REMOVE_PAGE_GROUP,
    {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    },
  );

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);

  const { errors } = formState;

  useEffect(() => {
    if (pageGroup) {
      setValue(
        'type',
        redirectTitleTypes.find(type => type?.value === pageGroup?.type),
      );
      if (pageGroup?.schedule_end) {
        setValue('schedule', true);
        setValue('schedule_start', new Date(pageGroup.schedule_start));
        setValue('schedule_end', new Date(pageGroup.schedule_end));
        setValue('countdown', pageGroup.countdown);
      }
      setValue('name', pageGroup.name);
      setValue('padding_top', pageGroup.padding_top);
      setValue('padding_right', pageGroup.padding_right);
      setValue('padding_left', pageGroup.padding_left);
      setValue('padding_bottom', pageGroup.padding_bottom);
      setValue('height', pageGroup.height);
      setValue('border_top', pageGroup?.border_top);
      setValue('border_bottom', pageGroup?.border_bottom);
      setValue('border_left', pageGroup?.border_left);
      setValue('border_right', pageGroup?.border_right);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageGroup]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, ...rest } = sanitizedObj;

      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,
              id: pageGroup.id,
              schedule_end: scheduleData ? rest.schedule_end : null,
              countdown: scheduleData ? rest.countdown : false,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
              type: data.type?.value,
              padding_top: Number(data.padding_top),
              padding_bottom: Number(data.padding_bottom),
              padding_right: Number(data.padding_right),
              padding_left: Number(data.padding_left),
            },
          },
        });
        if (pageGroup?.type !== data.type?.value) {
          if (pageGroup?.items?.length > 0) {
            await removeGroupElement({
              variables: {
                id: pageGroup?.items[0].id,
              },
            });
          }
        }

        toast({
          title: 'Sucesso!',
          description: 'Grupo editado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              element_display_id: display?.id,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
              type: data.type?.value,
              padding_top: Number(data.padding_top),
              padding_bottom: Number(data.padding_bottom),
              padding_right: Number(data.padding_right),
              padding_left: Number(data.padding_left),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: 'Sucesso!',
          description: 'Grupo criado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'Tem certeza que deseja excluir o grupo?',
      );
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: 'Sucess!!',
          description: 'Grupo deletado com sucesso',
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <>
      <VStack as="form" spacing="8" onSubmit={handleSubmit(handleForm)}>
        <SimpleGrid minHeight="24" minChildWidth="200px" spacing="8" w="100%">
          <Input
            label="Título"
            error={errors?.name}
            {...register('name')}
            isRequired
          />
          <Select
            name="type"
            control={control}
            menuPosition="absolute"
            isRequired
            label="Tipo"
            error={errors.type}
            options={redirectTitleTypes}
            onChange={() => {
              setValue('type', '');
            }}
          />
          <Input
            label="Altura (px)"
            type="number"
            error={errors?.height}
            {...register('height')}
          />
        </SimpleGrid>
        <Flex gridGap={6} w="100%" flexWrap="wrap">
          <Input
            label="Padding Esquerdo"
            type="number"
            placeholder="px"
            width="23%"
            error={errors?.padding_left}
            {...register('padding_left')}
          />
          <Input
            label="Padding Direito"
            placeholder="px"
            type="number"
            width="23%"
            error={errors?.padding_right}
            {...register('padding_right')}
          />
          <Input
            label="Padding Cima"
            placeholder="px"
            type="number"
            width="23%"
            error={errors?.padding_top}
            {...register('padding_top')}
          />
          <Input
            label="Padding Baixo"
            placeholder="px"
            type="number"
            width="23%"
            error={errors?.padding_bottom}
            {...register('padding_bottom')}
          />
        </Flex>
        <Flex gridGap={6} w="100%" flexWrap="wrap">
          <Input
            label="Borda Superior esquerda"
            placeholder="px"
            type="number"
            width="23%"
            error={errors?.border_left}
            {...register('border_left', { valueAsNumber: true })}
          />
          <Input
            label="Borda Superior direita"
            type="number"
            placeholder="px"
            width="23%"
            error={errors?.border_right}
            {...register('border_right', { valueAsNumber: true })}
          />
          <Input
            label="Borda inferior direita"
            type="number"
            placeholder="px"
            width="23%"
            error={errors?.border_top}
            {...register('border_top', { valueAsNumber: true })}
          />
          <Input
            label="Borda inferior esquerda"
            type="number"
            width="23%"
            placeholder="px"
            error={errors?.border_bottom}
            {...register('border_bottom', { valueAsNumber: true })}
          />
        </Flex>
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            Agendamento
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={() => setSchedule(state => !state)}
          />
        </FormControl>
        {schedule && (
          <>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_start')}
                label="Início"
                showTimeSelect
                error={errors?.schedule_start}
              />
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_end')}
                label="Final"
                showTimeSelect
                error={errors?.schedule_end}
              />
            </SimpleGrid>
            <Checkbox colorScheme="brand" checked {...register('countdown')}>
              Contagem Regressiva
            </Checkbox>
          </>
        )}
      </VStack>
      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>Fechar</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading || removeLoading}
            onClick={handleSubmit(handleForm)}
          >
            Salvar
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

export default Form;
